import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComments,
    faTimes,
    faSpinner,
    faCheck,
    faBug,
    faLightbulb,
    faPalette,
    faStar,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../../hooks/useFeedback';
import { useTranslation } from 'react-i18next';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { submitFeedback, loading: feedbackLoading, error: feedbackError } = useFeedback();

    const [feedbackSuccess, setFeedbackSuccess] = useState(false);
    const [feedbackForm, setFeedbackForm] = useState({
        type: 'general' as 'bug' | 'feature' | 'general' | 'ui',
        rating: 5,
        subject: '',
        message: ''
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user) {
            alert(t('components.feedbackModal.signInRequired'));
            return;
        }

        if (!feedbackForm.subject.trim() || !feedbackForm.message.trim()) {
            alert(t('components.feedbackModal.fillAllFields'));
            return;
        }

        try {
            await submitFeedback(feedbackForm);
            setFeedbackSuccess(true);

            // Reset form after 2 seconds
            setTimeout(() => {
                setFeedbackSuccess(false);
                onClose();
                setFeedbackForm({
                    type: 'general',
                    rating: 5,
                    subject: '',
                    message: ''
                });
            }, 2000);

        } catch (err) {
            console.error('Error submitting feedback:', err);
        }
    };

    const StarRating = ({ rating, onRatingChange }: { rating: number, onRatingChange: (rating: number) => void }) => {
        return (
            <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => onRatingChange(star)}
                        className={`text-2xl transition-colors ${
                            star <= rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'
                        } hover:text-yellow-400`}
                    >
                        <FontAwesomeIcon icon={faStar} />
                    </button>
                ))}
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    {rating} / 5
                </span>
            </div>
        );
    };

    const feedbackTypes = [
        { value: 'general', label: t('components.feedbackModal.feedbackTypes.general'), icon: faComments, color: 'text-blue-500' },
        { value: 'bug', label: t('components.feedbackModal.feedbackTypes.bug'), icon: faBug, color: 'text-red-500' },
        { value: 'feature', label: t('components.feedbackModal.feedbackTypes.feature'), icon: faLightbulb, color: 'text-yellow-500' },
        { value: 'ui', label: t('components.feedbackModal.feedbackTypes.ui'), icon: faPalette, color: 'text-purple-500' }
    ];

    if (!isOpen) return null;

    return (
        <div className="glass-modal-overlay">
            <div className="glass-modal max-w-2xl w-full">
                <div className="p-6">
                    {feedbackSuccess ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FontAwesomeIcon icon={faCheck} className="text-2xl text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                {t('components.feedbackModal.thankYou')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Your feedback helps us improve the app experience.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {t('components.feedbackModal.shareYourFeedback')}
                                </h2>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            {/* Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    {t('components.feedbackModal.feedbackType')}?
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {feedbackTypes.map((type) => (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => setFeedbackForm(prev => ({ ...prev, type: type.value as any }))}
                                            className={`p-3 rounded-lg border-2 transition-all ${
                                                feedbackForm.type === type.value
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <FontAwesomeIcon
                                                    icon={type.icon}
                                                    className={`${type.color} ${feedbackForm.type === type.value ? 'text-blue-600' : ''}`}
                                                />
                                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {type.label}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Rating */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    {t('components.feedbackModal.rateExperience')}?
                                </label>
                                <StarRating
                                    rating={feedbackForm.rating}
                                    onRatingChange={(rating) => setFeedbackForm(prev => ({ ...prev, rating }))}
                                />
                            </div>

                            {/* Subject */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('components.feedbackModal.subject')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={feedbackForm.subject}
                                    onChange={(e) => setFeedbackForm(prev => ({ ...prev, subject: e.target.value }))}
                                    placeholder={t('components.feedbackModal.subjectPlaceholder')}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Message */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('components.feedbackModal.message')} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={feedbackForm.message}
                                    onChange={(e) => setFeedbackForm(prev => ({ ...prev, message: e.target.value }))}
                                    placeholder={t('components.feedbackModal.messagePlaceholder')}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Error Message */}
                            {feedbackError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-sm text-red-600 dark:text-red-400">{feedbackError}</p>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex space-x-3">
                                <button
                                    type="submit"
                                    disabled={feedbackLoading}
                                    className="glass-btn-primary flex-1 flex items-center justify-center !px-4 !py-2"
                                >
                                    {feedbackLoading ? (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    ) : (
                                        <FontAwesomeIcon icon={faComments} className="mr-2" />
                                    )}
                                    {feedbackLoading ? t('components.feedbackModal.submitting') : t('components.feedbackModal.submitFeedback')}
                                </button>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="glass-btn-ghost !px-4 !py-2"
                                >
                                    {t('components.feedbackModal.cancel')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;